import { useEffect, useMemo } from 'react';

import { useAuth } from 'context/auth-context';
import useProfiles from 'hooks/profile/useProfiles';

interface UseAutoAthleteProfileSwitchResult {
  hasAthleteProfiles: boolean;
}

/**
 * Automatically switches to the first athlete profile for the given gym
 * if there is more than one athlete profile.
 *
 * **Profile Selection Priority:**
 * 1. Primary account (non-sub-account) - preferred
 * 2. First available athlete profile (including linked accounts) - fallback
 *
 * @param gymId - The ID of the gym to switch to.
 *
 * @returns - Profile switching result containing:
 * - `hasAthleteProfiles`: Whether the user has any athlete profiles in the specified gym
 **/
function useAutoAthleteProfileSwitchEffect(gymId: string | undefined): UseAutoAthleteProfileSwitchResult {
  const { session, selectProfile, clearProfile } = useAuth();

  const {
    data: profilesData,
    isSuccess,
    isLoading,
  } = useProfiles({
    user: session?.user.userId,
    role: 'athlete',
    athleteGym: gymId,
    includeLinked: true,
  });

  const profileIds = useMemo(() => {
    if (!profilesData?.pages) return [];
    return profilesData?.pages
      .map(page => page.data.results)
      .flat()
      .map(profile => profile.profileId)
      .join(',');
  }, [profilesData?.pages]);

  useEffect(() => {
    if (!session?.user || !gymId || isLoading || !isSuccess) return;

    const currentProfileId = session?.profile?.profileId;
    const athleteProfiles = profilesData?.pages.map(page => page.data.results).flat() ?? [];

    const primaryAccount = athleteProfiles.find(p => !p.isSubAccount);
    const targetProfile = primaryAccount || athleteProfiles[0];

    if (targetProfile) {
      if (currentProfileId !== targetProfile.profileId) {
        selectProfile(targetProfile.profileId);
      }

      return;
    }

    if (currentProfileId) {
      clearProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isLoading, profileIds, session?.user, session?.profile?.profileId, gymId]);

  return {
    hasAthleteProfiles: (profilesData?.pages[0]?.data.results.length ?? 0) > 0,
  };
}

export default useAutoAthleteProfileSwitchEffect;
